import { useEffect, useMemo, useRef, useState } from "react";
import { completeIntro, getNextCard, startLesson, submitAnswer } from "../api";

function normalizeToken(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[’]/g, "'")
    .replace(/[.,!?;:]+$/g, "")
    .replace(/\s+/g, " ");
}

function answersMatch(expected, received) {
  if (!Array.isArray(expected) || !Array.isArray(received)) return false;
  if (expected.length !== received.length) return false;

  return expected.every((part, index) => normalizeToken(part) === normalizeToken(received[index]));
}

function getVisibleHint(answerPart, hintCount) {
  const text = String(answerPart || "");
  const visibleLength = Math.min(1 + hintCount, text.length);
  return text.slice(0, visibleLength);
}

function formatTimeLeft(endsAt) {
  if (!endsAt) return "";
  const diffMs = new Date(endsAt).getTime() - Date.now();
  const safeMs = Math.max(0, diffMs);
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export default function LessonPage() {
  const [lessonId, setLessonId] = useState("");
  const [lessonEndsAt, setLessonEndsAt] = useState("");
  const [card, setCard] = useState(null);
  const [mode, setMode] = useState("");
  const [answers, setAnswers] = useState([]);
  const [hintCount, setHintCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [finished, setFinished] = useState(false);
  const [finishMessage, setFinishMessage] = useState("");
  const [remainingTime, setRemainingTime] = useState("");

  const autoSubmittingRef = useRef(false);

  const expectedAnswer = useMemo(() => {
    if (mode !== "practice") return [];
    return card?.practice?.answer || [];
  }, [mode, card]);

  const isLocallyCorrect = useMemo(() => {
    if (mode !== "practice") return false;
    if (!expectedAnswer.length) return false;
    return answersMatch(expectedAnswer, answers);
  }, [mode, expectedAnswer, answers]);

  useEffect(() => {
    if (!lessonEndsAt) return;

    setRemainingTime(formatTimeLeft(lessonEndsAt));

    const timer = setInterval(() => {
      setRemainingTime(formatTimeLeft(lessonEndsAt));
    }, 1000);

    return () => clearInterval(timer);
  }, [lessonEndsAt]);

  async function loadNextCard(activeLessonId) {
    setLoading(true);
    setError("");
    setStatus("");

    try {
      const data = await getNextCard(activeLessonId);

      if (data.status === "finished") {
        setFinished(true);
        setCard(null);
        setMode("");
        setFinishMessage(
          data.reason === "no_due_cards_left"
            ? "No due cards left for now."
            : data.message || "Lesson finished."
        );
        return;
      }

      setFinished(false);
      setFinishMessage("");
      setCard(data.card);
      setMode(data.mode);

      if (data.mode === "practice") {
        const size = Array.isArray(data.card?.practice?.answer)
          ? data.card.practice.answer.length
          : 0;

        setAnswers(Array(size).fill(""));
        setHintCount(0);
      } else {
        setAnswers([]);
        setHintCount(0);
      }
    } catch (err) {
      if (err.lessonFinished) {
        setFinished(true);
        setCard(null);
        setMode("");
        setFinishMessage("5 minutes are over. Good job. See you later.");
      } else {
        setError(err.message || "Failed to load next card");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleStartLesson() {
    setLoading(true);
    setError("");
    setStatus("");
    setFinished(false);
    setFinishMessage("");
    setCard(null);
    setMode("");
    setAnswers([]);
    setHintCount(0);

    try {
      const data = await startLesson();
      setLessonId(data.lessonId);
      setLessonEndsAt(data.endsAt);
      setStatus(`Lesson started. Due cards: ${data.dueCount}`);
      await loadNextCard(data.lessonId);
    } catch (err) {
      setError(err.message || "Failed to start lesson");
    } finally {
      setLoading(false);
    }
  }

  async function handleCompleteIntro(alreadyKnown = false) {
    if (!lessonId || !card?.id) return;

    setLoading(true);
    setError("");
    setStatus("");

    try {
      const data = await completeIntro(lessonId, card.id, alreadyKnown);

      if (data.lessonFinished) {
        setFinished(true);
        setCard(null);
        setMode("");
        setFinishMessage(data.message || "5 minutes are over. Good job. See you later.");
        return;
      }

      if (alreadyKnown) {
        setStatus("Marked as already known. Scheduled later.");
      }

      await loadNextCard(lessonId);
    } catch (err) {
      if (err.lessonFinished) {
        setFinished(true);
        setCard(null);
        setMode("");
        setFinishMessage("5 minutes are over. Good job. See you later.");
      } else {
        setError(err.message || "Failed to complete intro");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleAnswerChange(index, value) {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleHint() {
    setHintCount((prev) => prev + 1);
  }

  useEffect(() => {
    if (mode !== "practice") return;
    if (!lessonId || !card?.id) return;
    if (!isLocallyCorrect) return;
    if (autoSubmittingRef.current) return;

    let cancelled = false;

    async function sendCorrectAnswer() {
      autoSubmittingRef.current = true;
      setLoading(true);
      setError("");
      setStatus("Correct. Saving...");

      try {
        const data = await submitAnswer(lessonId, card.id, answers, hintCount);

        if (cancelled) return;

        if (data.lessonFinished) {
          setFinished(true);
          setCard(null);
          setMode("");
          setFinishMessage(data.message || "5 minutes are over. Good job. See you later.");
          setStatus("");
          return;
        }

        setStatus("Correct.");
        await loadNextCard(lessonId);
      } catch (err) {
        if (cancelled) return;

        if (err.lessonFinished) {
          setFinished(true);
          setCard(null);
          setMode("");
          setFinishMessage("5 minutes are over. Good job. See you later.");
        } else {
          setError(err.message || "Failed to submit answer");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          autoSubmittingRef.current = false;
        }
      }
    }

    sendCorrectAnswer();

    return () => {
      cancelled = true;
    };
  }, [mode, lessonId, card, answers, hintCount, isLocallyCorrect]);

  return (
    <div className="stack">
      <section className="card">
        <div className="row-between wrap">
          <div>
            <h2>Lesson</h2>
            <p className="muted">
              Current backend flow: start → next → intro/answer → next
            </p>
          </div>

          <div className="lesson-meta">
            {lessonId ? <div><strong>Lesson ID:</strong> {lessonId}</div> : null}
            {lessonEndsAt ? <div><strong>Time left:</strong> {remainingTime}</div> : null}
          </div>
        </div>

        <div className="row">
          <button onClick={handleStartLesson} disabled={loading}>
            {loading ? "Loading..." : "Start lesson"}
          </button>
        </div>

        {status ? <div className="alert success">{status}</div> : null}
        {error ? <div className="alert error">{error}</div> : null}
        {finished ? <div className="alert info">{finishMessage}</div> : null}
      </section>

      {card && mode === "learn" ? (
        <section className="card">
          <div className="row-between">
            <h3>{card.item}</h3>
            <span className="badge">{card.type}</span>
          </div>

          <div className="field">
            <label>Translate</label>
            <div>{card.translate}</div>
          </div>

          <div className="field">
            <label>Meaning</label>
            <div>{card.meaning}</div>
          </div>

          <div className="field">
            <label>Meaning RU</label>
            <div>{card.meaningRu}</div>
          </div>
          {card.note ? (
  <div className="field">
    <label>Note</label>
    <div>{card.note}</div>
  </div>
) : null}
          {Array.isArray(card.examples) && card.examples.length > 0 ? (
            <div className="field">
              <label>Examples</label>

              <div className="stack-sm">
                {card.examples.map((ex, index) => (
                  <div className="nested-box" key={index}>
                    <div>{ex.en}</div>
                    <div className="muted">{ex.ru}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="row wrap">
            <button onClick={() => handleCompleteIntro(false)} disabled={loading}>
              Continue
            </button>

            <button onClick={() => handleCompleteIntro(true)} disabled={loading}>
              I already know this word
            </button>
          </div>
        </section>
      ) : null}

      {card && mode === "practice" ? (
        <section className="card">
          <div className="row-between">
  <h3>{card.translate}</h3>
  <span className="badge">{card.type}</span>
</div>

          <div className="field">
            <label>English</label>
            <div className="sentence">{card.practice.en}</div>
          </div>

          <div className="field">
            <label>Russian</label>
            <div className="sentence">{card.practice.ru}</div>
          </div>
{card.note ? (
  <div className="field">
    <label>Note</label>
    <div>{card.note}</div>
  </div>
) : null}
          <div className="field">
            <label>Answer</label>

            <div className="answers-grid">
              {expectedAnswer.map((part, index) => (
                <div className="answer-box" key={index}>
                  <input
                    type="text"
                    value={answers[index] || ""}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    placeholder={`Part ${index + 1}`}
                    disabled={loading}
                  />
                  <div className="hint-line">
                    Hint: <strong>{getVisibleHint(part, hintCount)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="row">
            <button onClick={handleHint} disabled={loading}>
              Hint
            </button>
          </div>

          <div className="muted">
            {isLocallyCorrect
              ? "Correct. Saving..."
              : "You will move forward automatically after the correct answer."}
          </div>
        </section>
      ) : null}
    </div>
  );
}